// Copyright 2021-2022 zcloak authors & contributors
// SPDX-License-Identifier: Apache-2.0

import { type PaletteMode } from '@mui/material';
import { type ThemeOptions, alpha } from '@mui/material/styles';

type Func = (mode: PaletteMode) => NonNullable<ThemeOptions['components']>;

/**
 * Style overrides for Material UI components.
 *
 * @see https://github.com/mui-org/material-ui/tree/master/packages/mui-material/src
 */
const createComponents: Func = () => ({
  MuiCssBaseline: {
    styleOverrides: `
    * {
      box-sizing: border-box;
    }
    ::-webkit-scrollbar {
      width: 4px;
      height: 4px;
    }
    ::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 2px;
    }
    ::-webkit-scrollbar-thumb {
      background: #bfbfbf;
      border-radius: 10px;
    }
    ::-webkit-scrollbar-thumb:hover {
      background: #333;
    }
    ::-webkit-scrollbar-corner {
      background: #179a16;
    }
    @font-face {
      font-family: "Poppins";
      font-style: normal;
      font-display: swap;
      font-weight: 300;
      src: url("/fonts/Poppins-Light.ttf");
    }
    @font-face {
      font-family: "Poppins";
      font-style: normal;
      font-display: swap;
      font-weight: 400;
      src: url("/fonts/Poppins-Regular.ttf");
    }
    @font-face {
      font-family: "Poppins";
      font-style: normal;
      font-display: swap;
      font-weight: 500;
      src: url("/fonts/Poppins-Medium.ttf");
    }
    @font-face {
      font-family: "Poppins";
      font-style: normal;
      font-display: swap;
      font-weight: 600;
      src: url("/fonts/Poppins-SemiBold.ttf");
    }
    @font-face {
      font-family: "Poppins";
      font-style: normal;
      font-display: swap;
      font-weight: 700;
      src: url("/fonts/Poppins-Bold.ttf");
    }
    @font-face {
      font-family: "RobotoSlab";
      font-style: normal;
      font-display: swap;
      font-weight: 700;
      src: url("/fonts/RobotoSlab-Bold.ttf");
    }
    @font-face {
      font-family: "Roboto";
      font-style: normal;
      font-display: swap;
      font-weight: 400;
      src: url("/fonts/Roboto-Regular.ttf");
    }
    @font-face {
      font-family: "Papyrus";
      font-style: normal;
      font-display: swap;
      font-weight: 400;
      src: url("/fonts/PapyrusStd.OTF");
    }
  `
  },

  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 9,
        textTransform: 'initial',
        color: '#000',
        '&.Mui-disabled': {
          background: alpha('#747474', 0.26),
          color: '#fff'
        }
      },
      contained: {
        minWidth: '140px',
        background: 'linear-gradient(91deg, #7FFFEE 0%, #BAFE96 100%)'
      },
      sizeSmall: {
        minWidth: 120,
        padding: '4px 12px',
        fontSize: '0.875rem'
      },
      sizeMedium: {
        minWidth: 140,
        padding: '6px 14px',
        fontSize: '1rem'
      },
      sizeLarge: {
        minWidth: 160,
        padding: '12px 18px',
        fontSize: '1.125rem',
        fontWeight: 600
      }
    }
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '10px'
      }
    }
  },

  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme: { spacing } }) => ({
        justifyContent: 'center',
        padding: spacing(3),
        paddingTop: 0
      })
    }
  }
});

export { createComponents };
